// "Compile" action for an llm-agent node (the workflow compiler — see
// docs/compiler-plan.md). Kicks off a node compilation, polls the resulting
// Compilation until terminal, and shows the outcome inline: on SUCCEEDED a
// detached candidate WorkflowVersion is created (with the node swapped for a
// deterministic `compiled-agent`) which the operator can evaluate + promote;
// on FAILED the reason is shown.
//
// Compilation runs against the SAVED version of the workflow — save any
// in-flight edits first. Only rendered for `llm-agent` nodes in an editable
// (non-read-only) workflow.
import { useClient } from '@cortex/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { CodeViewer } from '../common/CodeViewer';
import { Spinner } from '../common/Spinner';
import { useToast } from '../common/Toast';

type CompilationStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

interface ICompilation {
  id: string;
  status: CompilationStatus;
  resultVersionId: string | null;
  code: string | null;
  toolsUsed: string[];
  hasFuzzyFallbacks: boolean;
  costUsd: string | null;
  error: { message?: string; kind?: string } | null;
}

export function CompileNodeButton({
  workflowId,
  nodeKey,
}: {
  workflowId: string;
  nodeKey: string;
}) {
  const client = useClient();
  const toast = useToast();
  const [compilationId, setCompilationId] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/workflows/${workflowId}/compile-node`, { json: { nodeKey } })
        .json<ICompilation>(),
    onSuccess: (c) => {
      setCompilationId(c.id);
      toast.show({ kind: 'info', text: `Compiling "${nodeKey}"…` });
    },
    onError: (err) => {
      toast.show({ kind: 'error', text: `Couldn't start compile: ${(err as Error).message}` });
    },
  });

  const compilation = useQuery({
    queryKey: ['compilation', workflowId, compilationId],
    enabled: !!compilationId,
    queryFn: () =>
      client.http
        .get(`v1/workflows/${workflowId}/compilations/${compilationId}`)
        .json<ICompilation>(),
    // Poll while in-flight; stop on a terminal state.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'PENDING' || s === 'RUNNING' ? 2_500 : false;
    },
  });

  const status = compilation.data?.status;
  const inFlight = start.isPending || status === 'PENDING' || status === 'RUNNING';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        onClick={() => {
          setCompilationId(null);
          start.mutate();
        }}
        disabled={inFlight}
        title="Compile this llm-agent node into a deterministic compiled-agent node (creates a candidate version to evaluate)."
      >
        {inFlight ? 'Compiling…' : status === 'SUCCEEDED' ? 'Re-compile' : 'Compile to code'}
      </button>

      {inFlight ? (
        <div
          className="t-secondary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            fontSize: 'var(--fs-xs)',
          }}
        >
          <Spinner inline={true} />
          Generating deterministic code and cutting a candidate version…
        </div>
      ) : null}

      {status === 'SUCCEEDED' && compilation.data ? (
        <div
          className="alert"
          style={{
            fontSize: 'var(--fs-xs)',
            background: 'var(--surface-2)',
            border: '1px solid var(--border-subtle)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-1)',
          }}
        >
          <strong>Compiled ✓</strong>
          <span>
            Candidate version created
            {compilation.data.resultVersionId ? ` (${compilation.data.resultVersionId})` : ''}. Open
            the Versions panel to evaluate or promote it.
          </span>
          <span>
            {compilation.data.toolsUsed.length} tool(s) ·{' '}
            {compilation.data.hasFuzzyFallbacks ? 'has llmFallback' : 'fully deterministic'}
            {compilation.data.costUsd ? ` · $${Number(compilation.data.costUsd).toFixed(4)}` : ''}
          </span>
          {compilation.data.code ? (
            <div className="field" style={{ marginTop: 'var(--sp-1)' }}>
              <span className="field-label">Generated code</span>
              <CodeViewer code={compilation.data.code} height={300} />
              <span className="field-help">
                Read-only — this is the deterministic body now running in the candidate
                version&apos;s <code className="t-mono">compiled-agent</code> node. Re-compile to
                regenerate.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {status === 'FAILED' ? (
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--status-danger)' }}>
          Compile failed: {compilation.data?.error?.message ?? 'unknown error'}
        </div>
      ) : null}
    </div>
  );
}
